import { useCallback, useSyncExternalStore } from "react";
import { useAppSelector } from "./store/hooks";
import { isCloudBoyAdmin } from "./store/slices/authSlice";

// Admin tools are opt-in, as in Waypoint's useAdminMode: the admin groups
// only offer the "Admin tools" switch (About, Preferences), and the Admin
// link and the admin-only bits elsewhere show only while it is on. It starts
// off, so an admin uses CloudBoy as a player by default. The stored switch
// never grants on its own: without the group it does nothing.
//
// This only decides what to show. play-api checks the group on every admin
// route, so the switch is a safety catch against stray taps, not a boundary.
// It stays on this device; it isn't one of the settings that follow the account.
export const ADMIN_MODE_KEY = "pocket-play.adminMode";

const listeners = new Set<() => void>();

function readSwitch(): boolean {
  try {
    return localStorage.getItem(ADMIN_MODE_KEY) === "true";
  } catch {
    return false;
  }
}

// Only in memory when storage is blocked, so the switch still works for the page.
let fallback: boolean | null = null;
const switchOn = () => fallback ?? readSwitch();

function subscribe(listener: () => void) {
  listeners.add(listener);
  // Another tab turning it on or off.
  window.addEventListener("storage", listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", listener);
  };
}

export function setAdminMode(on: boolean) {
  try {
    localStorage.setItem(ADMIN_MODE_KEY, on ? "true" : "false");
    fallback = null;
  } catch {
    fallback = on;
  }
  listeners.forEach((listener) => listener());
}

/** For code outside React (the crash screen): admin tools on, for these groups. */
export function adminModeOn(groups: string[]): boolean {
  return isCloudBoyAdmin(groups) && switchOn();
}

export interface AdminMode {
  /** The signed-in account is in an admin group, so the switch is offered. */
  isAdmin: boolean;
  /** An admin with the switch on: show the admin UI. */
  unlocked: boolean;
  setEnabled: (on: boolean) => void;
}

export function useAdminMode(): AdminMode {
  const { isAuthenticated, userGroups } = useAppSelector((s) => s.auth);
  const isAdmin = isAuthenticated && isCloudBoyAdmin(userGroups);
  const on = useSyncExternalStore(subscribe, switchOn);
  const setEnabled = useCallback((value: boolean) => setAdminMode(value), []);
  return { isAdmin, unlocked: isAdmin && on, setEnabled };
}
